import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { useHtmlDir } from '../organic/motion';
import { FOCUS_CSS, pageStyles as ps } from './fieldFormat';
import ReportIssueButton, { FlagIcon } from '../issues/ReportIssueButton';

/**
 * The frame of Field Mode's screens other than the camera: a header with a
 * way back and the screen's name, which takes the focus when the screen
 * opens so a screen reader says where the person now is.
 */
export default function FieldScreen({ title, backLabel, backTip, onBack, actions, children, testid }) {
  const heading = useRef(null);
  const dir = useHtmlDir();
  const { t } = useTranslation('viewAs');
  // Viewing the app as another role (utils/viewAs.js): nothing is kept here.
  const auth = useAuth();
  const viewing = !!auth?.viewAs;
  // At commit, not after paint: the screen arrives with the focus already on
  // its name, before anything else can run in between.
  useLayoutEffect(() => { heading.current?.focus?.(); }, []);
  return (
    <div className="fm-screen" style={ps.page} data-testid={testid}>
      <style>{FOCUS_CSS}</style>
      <header style={ps.header}>
        <button type="button" style={ps.headerBtn} title={backTip} aria-label={backLabel} onClick={onBack}
          data-testid="screen-back">
          <span aria-hidden="true">{dir === 'rtl' ? '→' : '←'}</span>
        </button>
        <h1 ref={heading} tabIndex={-1} style={ps.headerTitle}>{title}</h1>
        {actions}
        {/* Report issue, as every page's header has. */}
        {can(auth?.user, 'issue:report') && <ReportIssueButton style={ps.headerBtn}><FlagIcon /></ReportIssueButton>}
      </header>
      <main style={ps.main}>
        {viewing && (
          <p role="note" data-testid="field-view-as-note"
            style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 6, fontSize: 14,
              background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' }}>
            {t('field')}
          </p>
        )}
        {children}
      </main>
    </div>
  );
}
